import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { ScrollArea } from '../components/ui/scroll-area';
import { RichTextEditor } from '../components/ui/rich-text-editor';
import { TemplateLibrary, DocumentType } from '../components/ui/template-library';
import { LogoCropEditor } from '../components/ui/logo-crop-editor';
import { toast } from 'sonner';
import {
  Settings,
  Upload,
  Palette,
  Building2,
  Save,
  RefreshCw,
  Trash2,
  Eye,
  Image as ImageIcon,
  FileText,
  Shield,
  Cookie,
  ScrollText,
  AlertTriangle,
  Library,
  Download,
  History,
  X,
  Crop
} from 'lucide-react';
import { getErrorMessage } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

interface AssociationConfig {
  id: string;
  name: string;
  description?: string;
  logo_url?: string;
  primary_color: string;
  secondary_color: string;
  accent_color: string;
  text_color: string;
  background_color: string;
}

interface LegalDocuments {
  privacy_policy: string;
  cookie_policy: string;
  terms_of_service: string;
  disclaimer: string;
  privacy_policy_updated?: string;
  cookie_policy_updated?: string;
  terms_updated?: string;
  disclaimer_updated?: string;
}

const DEFAULT_COLORS = {
  primary_color: '#10b981',
  secondary_color: '#059669',
  accent_color: '#34d399',
  text_color: '#1f2937',
  background_color: '#ffffff'
};

const AssociationSettingsPage: React.FC = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState('branding');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [logoCropOpen, setLogoCropOpen] = useState(false);
  
  const [config, setConfig] = useState<AssociationConfig>({
    id: '',
    name: '',
    description: '',
    logo_url: '',
    ...DEFAULT_COLORS
  });
  
  // Documenti legali
  const [legalDocs, setLegalDocs] = useState<LegalDocuments | null>(null);
  const [editingDoc, setEditingDoc] = useState<keyof LegalDocuments | null>(null);
  const [docContent, setDocContent] = useState('');
  const [savingDoc, setSavingDoc] = useState(false);
  const [templateLibraryOpen, setTemplateLibraryOpen] = useState(false);
  const [templateDocType, setTemplateDocType] = useState<DocumentType>('privacy_policy');
  
  const [previewMode, setPreviewMode] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewContent, setPreviewContent] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [documentHistory, setDocumentHistory] = useState<any[]>([]);
  const [historyDocType, setHistoryDocType] = useState<string>('');

  // Mappa keyof LegalDocuments -> DocumentType
  const docTypeMap: Record<string, DocumentType> = {
    privacy_policy: 'privacy_policy',
    cookie_policy: 'cookie_policy',
    terms_of_service: 'terms_of_service',
    disclaimer: 'disclaimer',
  };

  const docTitleMap: Record<string, string> = {
    privacy_policy: 'Privacy Policy',
    cookie_policy: 'Cookie Policy',
    terms_of_service: 'Termini e Condizioni',
    disclaimer: 'Disclaimer',
  };

  const openTemplateLibrary = (docType: keyof LegalDocuments) => {
    setTemplateDocType(docTypeMap[docType] || 'privacy_policy');
    setTemplateLibraryOpen(true);
  };

  const handleSelectTemplate = (content: string) => {
    setDocContent(content);
  };

  // Preview con variabili sostituite
  const openPreview = async (docType: keyof LegalDocuments) => {
    try {
      const res = await axios.get(`${API}/associations/my-legal-documents/rendered`);
      const rendered = res.data.legal_documents?.[docType] || '';
      setPreviewContent(rendered);
      setPreviewTitle(docTitleMap[docType] || 'Documento');
      setPreviewOpen(true);
    } catch (error) {
      toast.error('Errore nel caricamento anteprima');
    }
  };

  // Export PDF
  const exportPDF = async (docType: keyof LegalDocuments) => {
    try {
      const res = await axios.get(`${API}/associations/my-legal-documents/rendered`);
      const content = res.data.legal_documents?.[docType] || '';
      const title = docTitleMap[docType];
      
      // Create printable HTML
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
          <head>
            <title>${title}</title>
            <style>
              body { font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; line-height: 1.6; }
              h1 { color: #1a1a1a; border-bottom: 2px solid #3b82f6; padding-bottom: 10px; }
              h2 { color: #374151; margin-top: 24px; }
              h3 { color: #4b5563; }
              ul, ol { padding-left: 24px; }
              li { margin-bottom: 8px; }
              strong { color: #1f2937; }
              p { margin-bottom: 12px; }
              @media print { body { padding: 20px; } }
            </style>
          </head>
          <body>
            ${content}
            <hr style="margin-top: 40px; border: none; border-top: 1px solid #e5e7eb;">
            <p style="color: #9ca3af; font-size: 12px; text-align: center;">
              Documento generato il ${new Date().toLocaleDateString('it-IT')}
            </p>
          </body>
          </html>
        `);
        printWindow.document.close();
        printWindow.print();
      }
    } catch (error) {
      toast.error('Errore nella generazione PDF');
    }
  };

  // Storico versioni
  const openHistory = async (docType: string) => {
    try {
      const res = await axios.get(`${API}/associations/my-legal-documents/history/${docType}`);
      setDocumentHistory(res.data.history || []);
      setHistoryDocType(docType);
      setHistoryOpen(true);
    } catch (error) {
      // Se non ci sono versioni, mostra lista vuota
      setDocumentHistory([]);
      setHistoryDocType(docType);
      setHistoryOpen(true);
    }
  };

  const restoreVersion = async (versionId: string) => {
    try {
      await axios.post(`${API}/associations/my-legal-documents/restore/${historyDocType}/${versionId}`);
      toast.success('Versione ripristinata');
      setHistoryOpen(false);
      fetchLegalDocuments();
    } catch (error) {
      toast.error('Errore nel ripristino');
    }
  };

  useEffect(() => {
    fetchAssociationConfig();
    fetchLegalDocuments();
  }, []);

  const fetchAssociationConfig = async () => {
    try {
      const res = await axios.get(`${API}/associations/my-association/config`);
      setConfig({
        ...DEFAULT_COLORS,
        ...res.data
      });
    } catch (error) {
      toast.error('Errore nel caricamento della configurazione');
    } finally {
      setLoading(false);
    }
  };

  const fetchLegalDocuments = async () => {
    try {
      const res = await axios.get(`${API}/associations/my-legal-documents`);
      if (res.data.legal_documents) {
        setLegalDocs(res.data.legal_documents);
      }
    } catch (error) {
      console.error('Error fetching legal docs:', error);
    }
  };

  const handleSaveLegalDoc = async (docType: keyof LegalDocuments) => {
    setSavingDoc(true);
    try {
      await axios.put(`${API}/associations/my-legal-documents`, {
        [docType]: docContent
      });
      toast.success('Documento salvato!');
      setEditingDoc(null);
      fetchLegalDocuments();
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSavingDoc(false);
    }
  };

  const handleResetLegalDoc = async (docType: string) => {
    try {
      await axios.post(`${API}/associations/my-legal-documents/reset?doc_type=${docType}`);
      toast.success('Documento resettato ai valori default');
      fetchLegalDocuments();
      if (editingDoc === docType) {
        setEditingDoc(null);
      }
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const openEditDoc = (docType: keyof LegalDocuments, content: string) => {
    setEditingDoc(docType);
    setDocContent(content);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/associations/my-association/config`, {
        name: config.name,
        description: config.description,
        logo_url: config.logo_url,
        primary_color: config.primary_color,
        secondary_color: config.secondary_color,
        accent_color: config.accent_color,
        text_color: config.text_color,
        background_color: config.background_color
      });
      toast.success('Configurazione salvata!');
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setSaving(false);
    }
  };

  const handleLogoUpload = async (croppedBlob: Blob) => {
    setUploading(true);
    const formData = new FormData();
    formData.append('file', croppedBlob, 'logo.png');

    try {
      const res = await axios.post(`${API}/associations/my-association/logo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setConfig({ ...config, logo_url: res.data.logo_url });
      toast.success('Logo caricato!');
    } catch (error) {
      toast.error(getErrorMessage(error));
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveLogo = async () => {
    try {
      await axios.delete(`${API}/associations/my-association/logo`);
      setConfig({ ...config, logo_url: '' });
      toast.success('Logo rimosso');
    } catch (error) {
      toast.error(getErrorMessage(error));
    }
  };

  const handleColorChange = (colorKey: string, value: string) => {
    setConfig({ ...config, [colorKey]: value });
  };

  const resetToDefaults = () => {
    setConfig({ ...config, ...DEFAULT_COLORS });
    toast.info('Colori ripristinati ai valori predefiniti');
  };

  // Applica i colori in preview
  const previewStyle = previewMode ? {
    '--preview-primary': config.primary_color,
    '--preview-secondary': config.secondary_color,
    '--preview-accent': config.accent_color,
  } as React.CSSProperties : {};

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="association-settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Settings className="h-6 w-6" />
            Impostazioni Associazione
          </h1>
          <p className="text-muted-foreground">
            Personalizza l'aspetto e i documenti legali della tua associazione
          </p>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="branding" className="gap-2">
            <Palette className="h-4 w-4" />
            Branding
          </TabsTrigger>
          <TabsTrigger value="legal" className="gap-2">
            <FileText className="h-4 w-4" />
            Documenti Legali
          </TabsTrigger>
        </TabsList>

        {/* TAB BRANDING */}
        <TabsContent value="branding" className="space-y-6 mt-6">
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => setPreviewMode(!previewMode)}
            >
              <Eye className="h-4 w-4 mr-2" />
              {previewMode ? 'Nascondi' : 'Anteprima'}
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? (
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Save className="h-4 w-4 mr-2" />
              )}
              Salva Modifiche
            </Button>
          </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Informazioni Base */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Building2 className="h-5 w-5" />
              Informazioni
            </CardTitle>
            <CardDescription>
              Nome e descrizione della tua associazione
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="name">Nome Associazione</Label>
              <Input
                id="name"
                value={config.name}
                onChange={(e) => setConfig({ ...config, name: e.target.value })}
                placeholder="Nome della tua associazione"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="description">Descrizione</Label>
              <Textarea
                id="description"
                value={config.description || ''}
                onChange={(e) => setConfig({ ...config, description: e.target.value })}
                placeholder="Breve descrizione dell'associazione..."
                rows={3}
              />
            </div>
          </CardContent>
        </Card>

        {/* Logo */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ImageIcon className="h-5 w-5" />
              Logo
            </CardTitle>
            <CardDescription>
              Carica il logo della tua associazione (formato rettangolare 300x100px)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-4">
              {/* Preview Logo */}
              <div 
                className="w-36 h-12 rounded-lg border-2 border-dashed border-muted-foreground/25 flex items-center justify-center overflow-hidden bg-muted/50"
              >
                {config.logo_url ? (
                  <img 
                    src={config.logo_url} 
                    alt="Logo" 
                    className="w-full h-full object-contain"
                  />
                ) : (
                  <div className="text-center p-2">
                    <ImageIcon className="h-6 w-6 mx-auto text-muted-foreground/50" />
                    <span className="text-xs text-muted-foreground">Nessun logo</span>
                  </div>
                )}
              </div>
              
              {/* Upload Controls */}
              <div className="flex-1 space-y-2">
                <Button
                  variant="outline"
                  className="w-full"
                  onClick={() => setLogoCropOpen(true)}
                  disabled={uploading}
                  data-testid="open-association-logo-editor-btn"
                >
                  {uploading ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Crop className="h-4 w-4 mr-2" />
                  )}
                  {config.logo_url ? 'Modifica Logo' : 'Carica Logo'}
                </Button>
                {config.logo_url && (
                  <Button
                    variant="ghost"
                    className="w-full text-destructive hover:text-destructive"
                    onClick={handleRemoveLogo}
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Rimuovi Logo
                  </Button>
                )}
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Formato rettangolare (3:1). Apparirà nella sidebar dei membri dell'associazione.
            </p>
            
            {/* Logo Crop Editor Dialog */}
            <LogoCropEditor
              open={logoCropOpen}
              onClose={() => setLogoCropOpen(false)}
              onSave={handleLogoUpload}
              cropShape="rect"
              aspectRatio={3}
              outputWidth={300}
              outputHeight={100}
              title="Modifica Logo Associazione"
            />
          </CardContent>
        </Card>

        {/* Color Set */}
        <Card className="md:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Palette className="h-5 w-5" />
                  Colori del Tema
                </CardTitle>
                <CardDescription>
                  Personalizza i colori della tua associazione. Questi colori saranno applicati a tutti i membri.
                </CardDescription>
              </div>
              <Button variant="ghost" size="sm" onClick={resetToDefaults}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Ripristina Default
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-5">
              {/* Primary Color */}
              <div className="space-y-2">
                <Label htmlFor="primary">Colore Primario</Label>
                <div className="flex gap-2">
                  <div 
                    className="w-10 h-10 rounded-lg border cursor-pointer"
                    style={{ backgroundColor: config.primary_color }}
                    onClick={() => document.getElementById('primary-picker')?.click()}
                  />
                  <input
                    id="primary-picker"
                    type="color"
                    value={config.primary_color}
                    onChange={(e) => handleColorChange('primary_color', e.target.value)}
                    className="sr-only"
                  />
                  <Input
                    id="primary"
                    value={config.primary_color}
                    onChange={(e) => handleColorChange('primary_color', e.target.value)}
                    className="font-mono text-sm"
                  />
                </div>
                <p className="text-xs text-muted-foreground">Pulsanti, link, accenti</p>
              </div>

              {/* Secondary Color */}
              <div className="space-y-2">
                <Label htmlFor="secondary">Colore Secondario</Label>
                <div className="flex gap-2">
                  <div 
                    className="w-10 h-10 rounded-lg border cursor-pointer"
                    style={{ backgroundColor: config.secondary_color }}
                    onClick={() => document.getElementById('secondary-picker')?.click()}
                  />
                  <input
                    id="secondary-picker"
                    type="color"
                    value={config.secondary_color}
                    onChange={(e) => handleColorChange('secondary_color', e.target.value)}
                    className="sr-only"
                  />
                  <Input
                    id="secondary"
                    value={config.secondary_color}
                    onChange={(e) => handleColorChange('secondary_color', e.target.value)}
                    className="font-mono text-sm"
                  />
                </div>
                <p className="text-xs text-muted-foreground">Hover, elementi secondari</p>
              </div>

              {/* Accent Color */}
              <div className="space-y-2">
                <Label htmlFor="accent">Colore Accento</Label>
                <div className="flex gap-2">
                  <div 
                    className="w-10 h-10 rounded-lg border cursor-pointer"
                    style={{ backgroundColor: config.accent_color }}
                    onClick={() => document.getElementById('accent-picker')?.click()}
                  />
                  <input
                    id="accent-picker"
                    type="color"
                    value={config.accent_color}
                    onChange={(e) => handleColorChange('accent_color', e.target.value)}
                    className="sr-only"
                  />
                  <Input
                    id="accent"
                    value={config.accent_color}
                    onChange={(e) => handleColorChange('accent_color', e.target.value)}
                    className="font-mono text-sm"
                  />
                </div>
                <p className="text-xs text-muted-foreground">Badge, notifiche</p>
              </div>

              {/* Text Color */}
              <div className="space-y-2">
                <Label htmlFor="text">Colore Testo</Label>
                <div className="flex gap-2">
                  <div 
                    className="w-10 h-10 rounded-lg border cursor-pointer"
                    style={{ backgroundColor: config.text_color }}
                    onClick={() => document.getElementById('text-picker')?.click()}
                  />
                  <input
                    id="text-picker"
                    type="color"
                    value={config.text_color}
                    onChange={(e) => handleColorChange('text_color', e.target.value)}
                    className="sr-only"
                  />
                  <Input
                    id="text"
                    value={config.text_color}
                    onChange={(e) => handleColorChange('text_color', e.target.value)}
                    className="font-mono text-sm"
                  />
                </div>
                <p className="text-xs text-muted-foreground">Testo principale</p>
              </div>

              {/* Background Color */}
              <div className="space-y-2">
                <Label htmlFor="bg">Sfondo</Label>
                <div className="flex gap-2">
                  <div 
                    className="w-10 h-10 rounded-lg border cursor-pointer"
                    style={{ backgroundColor: config.background_color }}
                    onClick={() => document.getElementById('bg-picker')?.click()}
                  />
                  <input
                    id="bg-picker"
                    type="color"
                    value={config.background_color}
                    onChange={(e) => handleColorChange('background_color', e.target.value)}
                    className="sr-only"
                  />
                  <Input
                    id="bg"
                    value={config.background_color}
                    onChange={(e) => handleColorChange('background_color', e.target.value)}
                    className="font-mono text-sm"
                  />
                </div>
                <p className="text-xs text-muted-foreground">Sfondo pagine</p>
              </div>
            </div>

            {/* Preview */}
            {previewMode && (
              <div className="mt-6 p-4 rounded-lg border" style={previewStyle}>
                <h3 className="font-medium mb-3">Anteprima</h3>
                <div className="flex flex-wrap gap-3">
                  <Button style={{ backgroundColor: config.primary_color, color: '#fff' }}>
                    Pulsante Primario
                  </Button>
                  <Button variant="outline" style={{ borderColor: config.primary_color, color: config.primary_color }}>
                    Pulsante Outline
                  </Button>
                  <Badge style={{ backgroundColor: config.accent_color, color: '#fff' }}>
                    Badge
                  </Badge>
                  <div 
                    className="px-4 py-2 rounded-lg"
                    style={{ backgroundColor: config.secondary_color, color: '#fff' }}
                  >
                    Elemento Secondario
                  </div>
                </div>
                <div className="mt-4 p-3 rounded" style={{ backgroundColor: config.background_color }}>
                  <p style={{ color: config.text_color }}>
                    Questo è un esempio di testo con i colori selezionati.
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Info Box */}
      <Card className="bg-blue-50 dark:bg-blue-950/30 border-blue-200">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <Palette className="h-5 w-5 text-blue-600 mt-0.5" />
            <div>
              <p className="font-medium text-blue-800 dark:text-blue-200">
                Colori condivisi con tutti i membri
              </p>
              <p className="text-sm text-blue-600 dark:text-blue-400">
                I colori configurati qui saranno applicati automaticamente a tutti i membri della tua associazione. 
                I membri non potranno modificare questi colori, garantendo un'identità visiva coerente.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
        </TabsContent>

        {/* TAB DOCUMENTI LEGALI */}
        <TabsContent value="legal" className="space-y-6 mt-6">
          <div className="grid gap-4 md:grid-cols-2">
            {/* Privacy Policy */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Shield className="h-5 w-5" />
                  Privacy Policy
                </CardTitle>
                <CardDescription>
                  Informativa sul trattamento dei dati personali
                  {legalDocs?.privacy_policy_updated && (
                    <span className="ml-2 text-xs">
                      (aggiornata il {new Date(legalDocs.privacy_policy_updated).toLocaleDateString('it-IT')})
                    </span>
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {editingDoc === 'privacy_policy' ? (
                  <>
                    <RichTextEditor
                      content={docContent}
                      onChange={setDocContent}
                      placeholder="Inserisci il testo della Privacy Policy..."
                      className="min-h-[300px]"
                      showTemplateButton={true}
                      onSelectTemplate={() => openTemplateLibrary('privacy_policy')}
                    />
                    <div className="flex gap-2">
                      <Button 
                        onClick={() => handleSaveLegalDoc('privacy_policy')} 
                        disabled={savingDoc}
                        size="sm"
                      >
                        {savingDoc ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                        Salva
                      </Button>
                      <Button variant="outline" onClick={() => setEditingDoc(null)} size="sm">
                        Annulla
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    <Button 
                      onClick={() => openEditDoc('privacy_policy', legalDocs?.privacy_policy || '')}
                      variant="outline"
                      size="sm"
                    >
                      Modifica
                    </Button>
                    <Button 
                      onClick={() => openPreview('privacy_policy')} 
                      variant="outline" 
                      size="sm"
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      Anteprima
                    </Button>
                    <Button 
                      onClick={() => exportPDF('privacy_policy')} 
                      variant="outline" 
                      size="sm"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      PDF
                    </Button>
                    <Button 
                      onClick={() => openHistory('privacy_policy')} 
                      variant="ghost" 
                      size="sm"
                    >
                      <History className="h-4 w-4 mr-2" />
                      Storico
                    </Button>
                    <Button 
                      onClick={() => handleResetLegalDoc('privacy_policy')} 
                      variant="ghost" 
                      size="sm"
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Reset
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Cookie Policy */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Cookie className="h-5 w-5" />
                  Cookie Policy
                </CardTitle>
                <CardDescription>
                  Informativa sull'utilizzo dei cookie
                  {legalDocs?.cookie_policy_updated && (
                    <span className="ml-2 text-xs">
                      (aggiornata il {new Date(legalDocs.cookie_policy_updated).toLocaleDateString('it-IT')})
                    </span>
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {editingDoc === 'cookie_policy' ? (
                  <>
                    <RichTextEditor
                      content={docContent}
                      onChange={setDocContent}
                      placeholder="Inserisci il testo della Cookie Policy..."
                      className="min-h-[300px]"
                      showTemplateButton={true}
                      onSelectTemplate={() => openTemplateLibrary('cookie_policy')}
                    />
                    <div className="flex gap-2">
                      <Button 
                        onClick={() => handleSaveLegalDoc('cookie_policy')} 
                        disabled={savingDoc}
                        size="sm"
                      >
                        {savingDoc ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                        Salva
                      </Button>
                      <Button variant="outline" onClick={() => setEditingDoc(null)} size="sm">
                        Annulla
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    <Button 
                      onClick={() => openEditDoc('cookie_policy', legalDocs?.cookie_policy || '')}
                      variant="outline"
                      size="sm"
                    >
                      Modifica
                    </Button>
                    <Button 
                      onClick={() => openPreview('cookie_policy')} 
                      variant="outline" 
                      size="sm"
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      Anteprima
                    </Button>
                    <Button 
                      onClick={() => exportPDF('cookie_policy')} 
                      variant="outline" 
                      size="sm"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      PDF
                    </Button>
                    <Button 
                      onClick={() => openHistory('cookie_policy')} 
                      variant="ghost" 
                      size="sm"
                    >
                      <History className="h-4 w-4 mr-2" />
                      Storico
                    </Button>
                    <Button 
                      onClick={() => handleResetLegalDoc('cookie_policy')} 
                      variant="ghost" 
                      size="sm"
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Reset
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Termini e Condizioni */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ScrollText className="h-5 w-5" />
                  Termini e Condizioni
                </CardTitle>
                <CardDescription>
                  Condizioni di utilizzo della piattaforma
                  {legalDocs?.terms_updated && (
                    <span className="ml-2 text-xs">
                      (aggiornate il {new Date(legalDocs.terms_updated).toLocaleDateString('it-IT')})
                    </span>
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {editingDoc === 'terms_of_service' ? (
                  <>
                    <RichTextEditor
                      content={docContent}
                      onChange={setDocContent}
                      placeholder="Inserisci il testo dei Termini e Condizioni..."
                      className="min-h-[300px]"
                      showTemplateButton={true}
                      onSelectTemplate={() => openTemplateLibrary('terms_of_service')}
                    />
                    <div className="flex gap-2">
                      <Button 
                        onClick={() => handleSaveLegalDoc('terms_of_service')} 
                        disabled={savingDoc}
                        size="sm"
                      >
                        {savingDoc ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                        Salva
                      </Button>
                      <Button variant="outline" onClick={() => setEditingDoc(null)} size="sm">
                        Annulla
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    <Button 
                      onClick={() => openEditDoc('terms_of_service', legalDocs?.terms_of_service || '')}
                      variant="outline"
                      size="sm"
                    >
                      Modifica
                    </Button>
                    <Button 
                      onClick={() => openPreview('terms_of_service')} 
                      variant="outline" 
                      size="sm"
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      Anteprima
                    </Button>
                    <Button 
                      onClick={() => exportPDF('terms_of_service')} 
                      variant="outline" 
                      size="sm"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      PDF
                    </Button>
                    <Button 
                      onClick={() => openHistory('terms_of_service')} 
                      variant="ghost" 
                      size="sm"
                    >
                      <History className="h-4 w-4 mr-2" />
                      Storico
                    </Button>
                    <Button 
                      onClick={() => handleResetLegalDoc('terms_of_service')} 
                      variant="ghost" 
                      size="sm"
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Reset
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Disclaimer */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="h-5 w-5" />
                  Disclaimer
                </CardTitle>
                <CardDescription>
                  Esclusione di responsabilità
                  {legalDocs?.disclaimer_updated && (
                    <span className="ml-2 text-xs">
                      (aggiornato il {new Date(legalDocs.disclaimer_updated).toLocaleDateString('it-IT')})
                    </span>
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {editingDoc === 'disclaimer' ? (
                  <>
                    <RichTextEditor
                      content={docContent}
                      onChange={setDocContent}
                      placeholder="Inserisci il testo del Disclaimer..."
                      className="min-h-[300px]"
                      showTemplateButton={true}
                      onSelectTemplate={() => openTemplateLibrary('disclaimer')}
                    />
                    <div className="flex gap-2">
                      <Button 
                        onClick={() => handleSaveLegalDoc('disclaimer')} 
                        disabled={savingDoc}
                        size="sm"
                      >
                        {savingDoc ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                        Salva
                      </Button>
                      <Button variant="outline" onClick={() => setEditingDoc(null)} size="sm">
                        Annulla
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    <Button 
                      onClick={() => openEditDoc('disclaimer', legalDocs?.disclaimer || '')}
                      variant="outline"
                      size="sm"
                    >
                      Modifica
                    </Button>
                    <Button 
                      onClick={() => openPreview('disclaimer')} 
                      variant="outline" 
                      size="sm"
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      Anteprima
                    </Button>
                    <Button 
                      onClick={() => exportPDF('disclaimer')} 
                      variant="outline" 
                      size="sm"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      PDF
                    </Button>
                    <Button 
                      onClick={() => openHistory('disclaimer')} 
                      variant="ghost" 
                      size="sm"
                    >
                      <History className="h-4 w-4 mr-2" />
                      Storico
                    </Button>
                    <Button 
                      onClick={() => handleResetLegalDoc('disclaimer')} 
                      variant="ghost" 
                      size="sm"
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Reset
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Info Box Legale */}
          <Card className="bg-amber-50 dark:bg-amber-950/30 border-amber-200">
            <CardContent className="py-4">
              <div className="flex items-start gap-3">
                <FileText className="h-5 w-5 text-amber-600 mt-0.5" />
                <div>
                  <p className="font-medium text-amber-800 dark:text-amber-200">
                    Documenti legali personalizzabili
                  </p>
                  <p className="text-sm text-amber-600 dark:text-amber-400">
                    I testi di default sono stati generati automaticamente e possono essere personalizzati per adattarsi 
                    alle esigenze specifiche della tua associazione. Si consiglia di consultare un legale per verificare 
                    la conformità dei documenti.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Template Library Modal */}
      <TemplateLibrary
        open={templateLibraryOpen}
        onOpenChange={setTemplateLibraryOpen}
        documentType={templateDocType}
        onSelectTemplate={handleSelectTemplate}
      />

      {/* Preview Modal */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5" />
              Anteprima - {previewTitle}
            </DialogTitle>
            <DialogDescription>
              Questa è l'anteprima del documento con le variabili dinamiche sostituite.
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[60vh] pr-4">
            <div 
              className="prose prose-sm max-w-none p-4 bg-white rounded-lg border"
              dangerouslySetInnerHTML={{ __html: previewContent }}
            />
          </ScrollArea>
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button variant="outline" onClick={() => setPreviewOpen(false)}>
              Chiudi
            </Button>
            <Button onClick={() => {
              const docType = Object.keys(docTitleMap).find(k => docTitleMap[k] === previewTitle);
              if (docType) exportPDF(docType as keyof LegalDocuments);
            }}>
              <Download className="h-4 w-4 mr-2" />
              Esporta PDF
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* History Modal */}
      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              Storico Versioni - {docTitleMap[historyDocType] || historyDocType}
            </DialogTitle>
            <DialogDescription>
              Visualizza le versioni precedenti del documento e ripristina se necessario.
            </DialogDescription>
          </DialogHeader>
          <ScrollArea className="max-h-[50vh]">
            {documentHistory.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <History className="h-12 w-12 mx-auto mb-4 opacity-20" />
                <p>Nessuna versione precedente disponibile.</p>
                <p className="text-sm">Le versioni verranno salvate automaticamente ad ogni modifica.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {documentHistory.map((version, index) => (
                  <div 
                    key={version.id || index}
                    className="p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium">
                          Versione del {new Date(version.created_at).toLocaleDateString('it-IT', {
                            day: '2-digit',
                            month: 'long',
                            year: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {version.content?.substring(0, 100)}...
                        </p>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => restoreVersion(version.id)}
                      >
                        Ripristina
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </ScrollArea>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AssociationSettingsPage;
